import React, { useEffect, useMemo, Suspense, useState } from 'react'
import '../style/App.scss'
import { useAuth0 } from '../contexts/auth0-context'
import { ThemeProvider, createTheme, responsiveFontSizes, Theme } from '@mui/material/styles'
import Dashboard from '../components/Dashboard/Dashboard'
import { BrowserRouter as Router, Route, Switch, Redirect } from 'react-router-dom'
import Evidence from '../components/Evidence/Evidence'
import Activities from '../components/Activities/Activities'
import StrategicImperatives from '../components/StrategicImperatives/StrategicImperatives'
import GapAnalysis from '../components/GapAnalysis/GapAnalysis'
import Layout from '../components/Support/Layout'
import HelpCenter from '../components/Support/Help'
import TppView from '../components/TppView'
import '../style/styles.scss'
import StrategySelection from '../components/StrategySelection/StrategySelection'
import Snapshots from '../components/Snapshots/Snapshots'
import SearchLandingPage from '../components/Search/SearchLandingPage'
import Tracelinks from '../components/Tracelinks/Tracelink'
import { CssBaseline } from '@mui/material'
import { connect, useSelector } from 'react-redux'
import { setSearchQuery } from '../redux/actions'
import AccountDisabledScreen from '../components/Support/AccountDisabledScreen'
import Logout from '../components/Support/Logout'
import { useDynamicFavicon } from './useDynamicFavicon'
import { FIGMA_DARK_COLORS, FIGMA_LIGHT_COLORS, FigmaColorsPalette } from '../style/colors'
import { useIsDarkMode } from '../shared/hooks/useIsDarkMode'
import ValueNarrativesView from '../components/ValueNarrativesView/ValueNarrativesView'
import UserSettingsViewAsync from '../components/UserSettingsView'
import PicoViewAsync from '../views/Pico'
import ValueCompendiumViewAsync from '../views/ValueCompendium'
import ChatBotViewAsync from '../views/ChatBot'
import ErrorBoundary from '../shared/components/ErrorBoundary/ErrorBoundary'
import AdminSplashScreen from '../components/AdminSplashScreen/AdminSplashScreen'
import { AdminPanel, AdminOrganizationDetail, AdminAuditLogs } from '../components/Admin'
import {
    shouldShowAdminSplash,
    getIsRecentlyLoggedIn,
    setAdminSplashSeen,
    getSeenSplashScreen,
} from '../utilities/adminSplashScreen'
import { useIsAdmin } from '../shared/hooks/useIsAdmin'
import { useIsUserDisabled } from '../shared/hooks/useIsUserDisabled'
import { useIdle } from '../shared/hooks/useIdle'

function mapDispatchToProps(dispatch) {
    return {
        setSearchQuery: (query) => dispatch(setSearchQuery(query)),
    }
}

declare module '@mui/material/styles' {
    interface Palette {
        figma: FigmaColorsPalette
    }
}

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        '12x400': true
        '14x400': true
        '14x500': true
        '14x600': true
        '14x700': true
        '16x400': true
        '16x500': true
        '16x600': true
        '16x700': true
        '18x500': true
        '18x600': true
        '18x400': true
        '18x700': true
        '20x500': true
        '20x700': true
        '22x300': true
        '22x500': true
        '22x600': true
        '22x700': true
        '24x500': true
        '28x600': true
        '28x700': true
        '32x500': true
        '40x400': true
    }
}

declare module '@mui/system' {
    interface DefaultTheme extends Theme {}
}

function ConnectedApp(props) {
    const { isLoading, user, loginWithRedirect, error: authError } = useAuth0()
    const isAdmin = useIsAdmin()
    const isIdle = useIdle()
    const [showAdminSplash, setShowAdminSplash] = useState(false)
    const isUserDisabled = useIsUserDisabled()

    useEffect(() => {
        // show splash when admin
        if (shouldShowAdminSplash(isAdmin)) {
            setShowAdminSplash(true)
        }
    }, [])

    useEffect(() => {
        // show splash when user is idle
        if (isIdle && isAdmin) {
            setShowAdminSplash(true)
        }
    }, [isIdle])

    const handleSplashClose = () => {
        setShowAdminSplash(false)
        setAdminSplashSeen()
    }

    useDynamicFavicon()

    const isDarkMode = useIsDarkMode()

    const getDesignTokens = (isDarkMode: boolean) => ({
        typography: {
            fontFamily: 'Acumin Pro, Arial',
            '12x400': {
                fontFamily: 'acumin-pro !important',
                fontSize: '12px',
                fontWeight: 400,
            },
            '14x400': {
                fontFamily: 'acumin-pro !important',
                fontSize: '14px',
                fontWeight: 400,
            },
            '14x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '14px',
                fontWeight: 500,
            },
            '14x600': {
                fontFamily: 'acumin-pro !important',
                fontSize: '14px',
                fontWeight: 600,
            },
            '14x700': {
                fontFamily: 'acumin-pro !important',
                fontSize: '15px',
                fontWeight: 700,
            },
            '16x400': {
                fontFamily: 'acumin-pro !important',
                fontSize: '16px',
                fontWeight: 400,
            },
            '16x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '16px',
                fontWeight: 500,
            },
            '16x600': {
                fontFamily: 'acumin-pro !important',
                fontSize: '16px',
                fontWeight: 600,
            },
            '16x700': {
                fontFamily: 'acumin-pro !important',
                fontSize: '16px',
                fontWeight: 700,
            },
            '18x400': {
                fontFamily: 'acumin-pro !important',
                fontSize: '18px',
                fontWeight: 400,
            },
            '18x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '18px',
                fontWeight: 500,
            },
            '18x600': {
                fontFamily: 'acumin-pro !important',
                fontSize: '18px',
                fontWeight: 600,
            },
            '18x700': {
                fontFamily: 'acumin-pro !important',
                fontSize: '18px',
                fontWeight: 700,
            },
            '20x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '20px',
                fontWeight: 500,
            },
            '20x700': {
                fontFamily: 'acumin-pro !important',
                fontSize: '20px',
                fontWeight: 700,
            },
            '22x300': {
                fontFamily: 'acumin-pro !important',
                fontSize: '22px',
                fontWeight: '300',
            },
            '22x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '22px',
                fontWeight: '500',
            },
            '22x600': {
                fontFamily: 'acumin-pro !important',
                fontSize: '22px',
                fontWeight: '600',
            },
            '22x700': {
                fontFamily: 'acumin-pro !important',
                fontSize: '22px',
                fontWeight: '700',
            },
            '24x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '24px',
                fontWeight: '500',
            },
            '28x600': {
                fontFamily: 'acumin-pro !important',
                fontSize: '28px',
                fontWeight: '600',
            },
            '28x700': {
                fontFamily: 'acumin-pro !important',
                fontSize: '28px',
                fontWeight: '700',
            },
            '32x500': {
                fontFamily: 'acumin-pro !important',
                fontSize: '32px',
                fontWeight: '500',
            },
            '40x400': {
                fontFamily: 'acumin-pro !important',
                fontSize: '40px',
                fontWeight: '400',
            },
            h1: {
                fontSize: '28px',
                fontWeight: '600',
            },
            h2: {
                fontSize: '22px',
                fontWeight: '600',
            },
            h3: {
                fontSize: '22px',
                fontWeight: '500',
            },
            h4: {
                fontSize: '20px',
                fontWeight: '500',
            },
            h5: {
                fontSize: '18px',
                fontWeight: '600',
            },
            h6: {
                fontSize: '12px',
                fontWeight: '300',
            },
            subtitle1: {
                fontSize: '18px',
                fontWeight: '400',
            },
            subtitle2: {
                fontSize: '16px',
                fontWeight: '500',
            },
            subtitle3: {
                fontSize: '14px',
                fontWeight: '500',
            },
            subtitle4: {
                fontSize: '12px',
                fontWeight: '600',
            },
            subtitle5: {
                fontSize: '12px',
                fontWeight: '400',
            },
            title1: {
                fontSize: '18px',
                fontWeight: '500',
            },
            title2: {
                fontSize: '16px',
                fontWeight: '600',
            },
            title3: {
                fontSize: '22px',
                fontWeight: '700',
            },
            body1: {
                fontSize: '18px',
                fontWeight: '500',
            },
            body2: {
                fontSize: '18px',
                fontWeight: '400',
            },
            body3: {
                fontSize: '16px',
                fontWeight: '500',
            },
            body4: {
                fontSize: '16px',
                fontWeight: '400',
            },
            body5: {
                fontSize: '14px',
                fontWeight: '500',
            },
            body6: {
                fontSize: '14px',
                fontWeight: '400',
            },
            body7: {
                fontSize: '32px',
                fontWeight: '300',
            },
            body8: {
                fontSize: '24px',
                fontWeight: '500',
            },
            body9: {
                fontSize: '22px',
                fontWeight: '300',
            },
            button: {
                fontSize: '16px',
                fontWeight: '600',
            },
            menu1: { fontSize: '18px', fontWeight: '600' },
            menu2: { fontSize: '16px', fontWeight: '400' },
            chip: { fontSize: '14px', fontWeight: '400' },
            caption: {
                fontSize: 18,
                fontWeight: '400',
            },
            overline: {
                fontSize: 14,
                fontWeight: '400',
            },
        },
        palette: {
            ...(isDarkMode
                ? {
                      //   palette values for dark mode
                      type: 'dark',
                      figma: FIGMA_DARK_COLORS,
                      primary: {
                          main: FIGMA_DARK_COLORS.primary,
                      },
                      secondary: {
                          main: '#ffffff',
                      },
                      background: {
                          paper: '#1f2127',
                          default: FIGMA_DARK_COLORS.grayscale.contentWrapper,
                          table: '#0049EA',
                      },
                      divider: 'rgba(255, 255, 255,.2)',
                      text: {
                          primary: '#ffffff',
                          secondary: FIGMA_DARK_COLORS.primary,
                      },
                  }
                : {
                      //   palette values for light mode
                      type: 'light',
                      figma: FIGMA_LIGHT_COLORS,
                      primary: {
                          main: FIGMA_LIGHT_COLORS.primary,
                      },
                      secondary: {
                          main: '#000000',
                      },
                      divider: 'rgba(0,0,0,.2)',
                      text: {
                          primary: '#4D4D4D',
                          secondary: '#000000',
                      },
                  }),
        },
    })

    let theme = useMemo(() => {
        const designTokens = getDesignTokens(isDarkMode)
        return createTheme(designTokens, {
            components: {
                MuiOutlinedInput: {
                    styleOverrides: {
                        root: {
                            borderRadius: '12px',
                        },
                    },
                },
                MuiFormHelperText: {
                    styleOverrides: {
                        root: {
                            color: 'grey',
                            fontStyle: 'italic',
                        },
                    },
                },
                MuiSvgIcon: {
                    defaultProps: {
                        title: '',
                    },
                },
                MuiAppBar: {
                    styleOverrides: {
                        root: {
                            backgroundColor: isDarkMode ? '#000000' : '#F2F6FE',
                        },
                    },
                },
                MuiChip: {
                    styleOverrides: {
                        defaultProps: {
                            backgroundColor: isDarkMode ? '#000000' : '#EBF1FD',
                        },
                    },
                },
                MuiAccordionSummary: {
                    styleOverrides: {
                        root: {
                            backgroundColor: isDarkMode ? '#000000' : '#EBF1FD',
                        },
                    },
                },
                MuiDialog: {
                    styleOverrides: {
                        paper: {
                            borderRadius: '16px',
                        },
                    },
                },
                MuiAccordion: {
                    variants: [
                        {
                            props: { variant: 'rounded-bottom' },
                            style: {
                                boxShadow: 'none',
                                width: '100%',
                                borderBottomLeftRadius: '24px',
                                borderBottomRightRadius: '24px',
                                backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6',
                            },
                        },
                    ],
                },
                MuiGrid: {
                    variants: [
                        {
                            props: { variant: 'rounded-top-grid' },
                            style: {
                                backgroundColor: isDarkMode ? '#5E6169' : '#F5F5F6',
                                borderTopLeftRadius: '16px',
                                borderTopRightRadius: '16px',
                            },
                        },
                    ],
                },
                MuiButton: {
                    variants: [
                        {
                            props: { variant: 'no-outline-sm-button' }, // Custom variant name
                            style: {
                                borderRadius: '14px',
                                width: '113px',
                                height: '52px',
                                color: isDarkMode ? 'white' : '#0049EA',
                                boxShadow: 'none',
                                border: 'none',
                            },
                        },
                        {
                            props: { variant: 'outlined-sm-button' }, // Custom variant name
                            style: {
                                borderRadius: '14px',
                                width: '113px',
                                height: '52px',
                                color: isDarkMode ? 'white' : '#0049EA',
                                border: isDarkMode ? '1px solid #389BFF' : '1px solid #0049EA',
                                boxShadow: 'none',
                            },
                        },
                        {
                            props: { variant: 'contained-sm-button' }, // Custom variant name
                            style: {
                                borderRadius: '14px',
                                width: '113px',
                                height: '52px',
                                color: 'white',
                                backgroundColor: isDarkMode ? '#389BFF' : '#0049EA',
                                border: '1px solid #0049EA',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: isDarkMode ? '#55B8F9' : '#002984',
                                    boxShadow: 3,
                                },
                            },
                        },
                        {
                            props: { variant: 'contained-big-button' },
                            style: {
                                borderRadius: '16px',
                                width: '250px',
                                height: '52px',
                                color: 'white',
                                backgroundColor: isDarkMode ? '#389BFF' : '#0049EA',
                                border: '1px solid #0049EA',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: isDarkMode ? '#55B8F9' : '#002984',
                                    boxShadow: 3,
                                },
                            },
                        },
                        {
                            props: { variant: 'contained-link-unlink' },
                            style: {
                                borderRadius: '9px',
                                width: '72px',
                                height: '32px',
                                color: 'white',
                                backgroundColor: designTokens.palette.figma.primary,
                                border: `1px solid ${designTokens.palette.figma.primary}`,
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: designTokens.palette.figma.primaryHover,
                                    boxShadow: 3,
                                },
                            },
                        },
                        {
                            props: { variant: 'contained-sort' },
                            style: {
                                borderRadius: '12px',
                                minWidth: 'auto',
                                height: '42px',
                                color: 'white',
                                backgroundColor: isDarkMode ? '#389BFF' : '#0049EA',
                                border: '1px solid #0049EA',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: isDarkMode ? '#1968AF' : '#002984',
                                    boxShadow: 3,
                                },
                            },
                        },
                    ],
                    styleOverrides: {
                        root: {
                            borderRadius: '14px',
                            '&.Mui-disabled': {
                                color: 'inherit',
                                opacity: 0.26,
                            },
                        },
                    },
                },
            },
            props: {
                MuiAppBar: {
                    color: 'inherit',
                },
                MuiChip: {
                    color: 'inherit',
                },
            },
        })
    }, [isDarkMode])
    theme = responsiveFontSizes(theme)

    useEffect(() => {
        ;(async function login() {
            if (!isLoading && !user && !authError) {
                await loginWithRedirect()
            }
        })()
    }, [isLoading, user, loginWithRedirect, authError])

    function navigateAwayFromSearch() {
        props.setSearchQuery('')
    }

    if (isUserDisabled) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <AccountDisabledScreen />
            </ThemeProvider>
        )
    }

    return (
        <>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Suspense fallback={null}>
                    <Router>
                        <ErrorBoundary>
                            <AdminSplashScreen
                                isVisible={showAdminSplash}
                                onClose={handleSplashClose}
                                getIsRecentlyLoggedIn={getIsRecentlyLoggedIn}
                            />
                            <Switch>
                                <Route path="/pico" component={PicoViewAsync} />
                                <Route path="/vc" component={ValueCompendiumViewAsync} />
                                <Route path="/maia" component={ChatBotViewAsync} />
                                <Route path="*">
                                    <Layout>
                                        <Switch>
                                            <Route exact path="/" component={Dashboard} />
                                            <Route path="/dashboard" component={Dashboard} />
                                            <Route path="/roadmaps" exact component={StrategySelection} />
                                            <Route path="/evidence" component={Evidence} />
                                            <Route path="/narratives" component={ValueNarrativesView} />
                                            <Route path="/activities" component={Activities} />
                                            <Route path="/strategic-imperatives" component={StrategicImperatives} />
                                            <Route path="/gap-analysis" component={GapAnalysis} />
                                            <Route path="/settings" component={UserSettingsViewAsync} />
                                            <Route path="/snapshots" component={Snapshots} />
                                            <Route path="/tracelink" component={Tracelinks} />
                                            <Route path="/help" component={HelpCenter} />
                                            <Route path="/tpp" component={TppView} />
                                            <Route path="/reset" component={Logout} />
                                            <Route path="/admin" exact component={AdminPanel} />
                                            <Route
                                                path="/admin/organizations/:orgId"
                                                component={AdminOrganizationDetail}
                                            />
                                            <Route path="/admin/audit-logs" component={AdminAuditLogs} />
                                            <Route
                                                path="/search"
                                                component={SearchLandingPage}
                                                onLeave={navigateAwayFromSearch}
                                            />
                                            <Redirect from="*" to="/" />
                                        </Switch>
                                    </Layout>
                                </Route>
                            </Switch>
                        </ErrorBoundary>
                    </Router>
                </Suspense>
            </ThemeProvider>
        </>
    )
}

const App = connect(null, mapDispatchToProps)(ConnectedApp)

export default App
